import React, { useState, useEffect, useCallback } from "react";
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from "react-router-dom";
import { Web3ReactProvider } from "@web3-react/core";
import { Web3Provider } from "@ethersproject/providers";
import ConnectedWalletContext from "./ConnectedWalletContext";
import { WelcomeDialog } from "./components/dialogs/WelcomeDialog";
import ContractContext from './ContractContext';
import DataContext from "./DataContext";

import { Rocketfi } from './components/Rocketfi';
import { Floatie } from './components/Floatie';
import Radar from './components/Radar';
import { Refuel } from './components/Refuel';
import { Ultra } from './components/Ultra';
import NavigationBar from './NavigationBar';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

function getLibrary(provider) {
  const library = new Web3Provider(provider);
  library.pollingInterval = 12000;
  return library;
}

const containerStyle = {
  maxWidth: '1920px', // Adjust this value according to your preference
  margin: '0 auto', // Centers the container
  width: '100%', // Takes full width on smaller screens
};



function App() {
  // State hooks for application-wide state management
  const [contracts, setContracts] = useState({});
  const [data, setData] = useState({});
  const [defiApp, setDefiApp] = useState(localStorage.getItem('defiApp') || "Radar");
  const [connectedAddress, setConnectedAddress] = useState(null);
  const [backgroundStyle, setBackgroundStyle] = useState({});



  // A memoized callback for setting theme, avoids recreation on each render
  const changeTheme = useCallback((theme) => {
    let themeLink = document.getElementById('theme-link');
    if (themeLink) {
      themeLink.href = `${theme}.css`;
    }
  }, []);

  // Centralized logic to update theme and background based on the defiApp state
  const updateDefiApp = useCallback((app) => {
    setDefiApp(app);
    localStorage.setItem('defiApp', app);
    const themeStyles = {
      "RocketFi": { background: '' },
      "Refuel": { background: '' },
      "Floatie": { background: '' },
      "Ultra": { background: '' },
      // Add cases for other dApps as required
      default: {}
    };
    setBackgroundStyle(themeStyles[app] || themeStyles.default);
    changeTheme(`/themes/${app.toLowerCase()}/theme`);
  }, [changeTheme]);

  // Effect hook for initializing defiApp state from local storage
  useEffect(() => {
    const savedDefiApp = localStorage.getItem('defiApp');
    if (savedDefiApp && savedDefiApp !== defiApp) {
      setDefiApp(savedDefiApp);
    }
  }, []);

  useEffect(() => {
    defiApp === "RocketFi" || defiApp === "Radar" || defiApp === "Refuel" || defiApp === "Ultra"
      ? changeTheme('/themes/rocketfi/dark/theme')
      : changeTheme('/themes/floatie/light/theme');
  }, [defiApp]);

  return (
    <div style={containerStyle}>
      <Web3ReactProvider getLibrary={getLibrary}>
        <ContractContext.Provider value={{ contracts, setContracts }}>
          <DataContext.Provider value={{ data, setData }}>
            <ConnectedWalletContext.Provider value={{ connectedAddress, setConnectedAddress }}>
              <Router basename="/">
                <UseLocationComponent updateDefiApp={updateDefiApp} />
                <div style={{ ...backgroundStyle, position: 'relative' }}>
                  <WelcomeDialog />
                  <NavigationBar changeTheme={changeTheme} updateDefiApp={updateDefiApp} defiApp={defiApp} />
                  <Routes>
                    <Route path="/rocketfi" element={<Rocketfi />} />
                    <Route path="/radar" element={<Radar />} />
                    <Route path="/refuel" element={<Refuel />} />
                    <Route path="/floatie" element={<Floatie />} />
                    <Route path="/ultra" element={<Ultra />} />
                    <Route path="/" element={<Radar />} />
                  </Routes>
                </div>
              </Router>
            </ConnectedWalletContext.Provider>
          </DataContext.Provider>
        </ContractContext.Provider>
      </Web3ReactProvider>
      <ToastContainer
        position="top-right"
        autoClose={2500} // Default autoClose for non-persistent toasts
        hideProgressBar={true} // Hide progress bar globally
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </div>
  );
}


export default App;

// Define a new component that will actually use the location
function UseLocationComponent({ updateDefiApp }) {
  const location = useLocation();

  useEffect(() => {
    // Map paths to corresponding DeFi app names
    const pathToAppMap = {
      '/rocketfi': 'RocketFi',
      '/radar': 'Radar',
      '/refuel': 'Refuel',
      '/floatie': 'Floatie',
      '/ultra': 'Ultra',
      // ...other mappings
    };

    const appFromPath = pathToAppMap[location.pathname] || "Radar"; // Default to Radar if no match
    updateDefiApp(appFromPath); // Use the updateDefiApp function to change the theme
  }, [location, updateDefiApp]); // Add updateDefiApp as a dependency

  return null;
}


